import React, { useRef, useState, useCallback, useEffect } from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import './MapView.css';
import { useWebSocket } from '../contexts/WebSocketContext';

// Import types
import { PilotData, MapViewProps } from '../types';

// Import extracted components
import LoadingOverlay from './map/LoadingOverlay';
import StatusNotification from './map/StatusNotification';
import RaceNotFound from './map/RaceNotFound';
import PilotSidebar from './pilots/PilotSidebar';
import Map3DTerrain from './map/Map3DTerrain';
import TimeOverlay from './map/TimeOverlay';

// Import custom hooks
import { useMapSetup } from '../hooks/useMapSetup';
import { usePilotMarkers } from '../hooks/usePilotMarkers';
import { useTrackManager } from '../hooks/useTrackManager';
import { usePilotPopup } from '../hooks/usePilotPopup';
import { useTaskMarkers } from '../hooks/useTaskMarkers';
import { use3DControl } from '../hooks/use3DControl';
import { useMapStyleControl } from '../hooks/useMapStyleControl';
import { useLabelsControl } from '../hooks/useLabelsControl';
import { useFullscreenControl } from '../hooks/useFullscreenControl';
import { useMapStyleManager } from '../hooks/useMapStyleManager';
import { useTask3D } from '../hooks/useTask3D';

// Import utilities
// MVT layers are now loaded on-demand, not automatically

const MapViewRefactored: React.FC<MapViewProps> = React.memo(({
  raceCenter,
  raceId,
  selectedTask,
  showTask = true,
  delayedTime
}) => {
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const mapContainerWrapper = useRef<HTMLDivElement | null>(null);
  const raceIdRef = useRef(raceId);
  const { pilots, pilotsLoading, waitingForPilots, raceNotFound } = useWebSocket();

  // Update ref when raceId changes (though it shouldn't during a session)
  useEffect(() => {
    raceIdRef.current = raceId;
  }, [raceId]);

  // NO INTERPOLATION - use raw pilot data directly
  // Pilots will jump to new positions when updates arrive
  const pilotsToUse = pilots;


  // State
  const [selectedPilot, setSelectedPilot] = useState<string | null>(null);
  const [followingPilot, setFollowingPilot] = useState<string | null>(null);
  const [is3D, setIs3D] = useState(false);
  const [is3DLoading, setIs3DLoading] = useState(false);
  const [mapStyle, setMapStyle] = useState('osm');
  const [showTerrainLabels, setShowTerrainLabels] = useState(false); // Default to off in 3D
  const [mapFullyReady, setMapFullyReady] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Calculate initial bounds from pilots - only once on mount
  const initialBounds = React.useMemo(() => {
    // Use an empty dependency array to calculate this only once
    if (pilots.size === 0) return null;

    let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
    let hasValidPilots = false;

    pilots.forEach(pilot => {
      if (pilot.lat && pilot.lng) {
        minLat = Math.min(minLat, pilot.lat);
        maxLat = Math.max(maxLat, pilot.lat);
        minLng = Math.min(minLng, pilot.lng);
        maxLng = Math.max(maxLng, pilot.lng);
        hasValidPilots = true;
      }
    });

    return hasValidPilots ? [[minLng, minLat], [maxLng, maxLat]] as [[number, number], [number, number]] : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - calculate only once

  // Set up map
  const { map, mapLoaded, styleLoaded } = useMapSetup({
    container: mapContainer,
    center: raceCenter,
    initialBounds
  });

  // Map is fully ready only when both map and style are loaded
  useEffect(() => {
    // Map ready check
    if (mapLoaded && styleLoaded) {
      // Add a small delay to ensure everything is truly ready
      const timer = setTimeout(() => {
        setMapFullyReady(true);
        // Map fully ready
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [mapLoaded, styleLoaded]);

  // Listen for pilots arrival event and auto-focus
  useEffect(() => {
    const handlePilotsArrived = (event: CustomEvent) => {
      if (event.detail.firstPilotLoad && map && mapFullyReady && pilots.size > 0) {
        // Auto-focusing on pilots

        // Calculate bounds from all pilots
        let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
        pilots.forEach(pilot => {
          if (pilot.lat && pilot.lng) {
            minLat = Math.min(minLat, pilot.lat);
            maxLat = Math.max(maxLat, pilot.lat);
            minLng = Math.min(minLng, pilot.lng);
            maxLng = Math.max(maxLng, pilot.lng);
          }
        });

        // Fit map to pilots with padding
        if (minLat < 90) {
          map.fitBounds(
            [[minLng, minLat], [maxLng, maxLat]],
            { padding: 100, duration: 1000 }
          );
        }

        // Auto-open sidebar
        setSidebarOpen(true);

        // Show a subtle notification
        // Pilots detected - opening sidebar
      }
    };

    window.addEventListener('pilotsArrived', handlePilotsArrived as EventListener);
    return () => {
      window.removeEventListener('pilotsArrived', handlePilotsArrived as EventListener);
    };
  }, [map, mapFullyReady, pilots]);

  // Handle pilot centering
  const handleCenterPilot = useCallback((pilot: PilotData) => {
    if (map) {
      map.flyTo({
        center: [pilot.lng, pilot.lat],
        zoom: 15,
        duration: 1500
      });
    }
  }, [map]);

  // Set up pilot markers - only when map is fully ready
  const { markers } = usePilotMarkers({
    map: mapFullyReady ? map : null,
    pilots: mapFullyReady ? pilotsToUse : new Map(),
    followingPilot,
    onFirstMarker: () => { /* First marker added */ },
    is3D // Pass 3D state to control visibility
  });

  // Set up track manager for handling live and historical tracks
  const { trackManager, loadHistoricalTrack, toggleHistoricalTrack } = useTrackManager({
    map: mapFullyReady ? map : null,
    pilots: mapFullyReady ? pilotsToUse : new Map(),
    raceId: raceId
  });

  // Set up centralized map style management
  const { switchToStyle } = useMapStyleManager({
    map: mapFullyReady ? map : null,
    trackManager,
    is3D
  });

  // Set up pilot popup - only when map is fully ready
  usePilotPopup({
    map: mapFullyReady ? map : null,
    pilots: mapFullyReady ? pilotsToUse : new Map(),
    markers,
    onLoadHistory: loadHistoricalTrack,
    onToggleHistory: toggleHistoricalTrack,
    trackManager,
    onCenterPilot: handleCenterPilot,
    onFollowPilot: setFollowingPilot,
    followingPilot
  });


  // Handle 3D terrain toggle
  const toggle3D = useCallback(() => {
    setIs3D(prev => {
      if (prev) {
        // Going from 3D to 2D - default to OSM with labels visible
        setShowTerrainLabels(true);
        setMapStyle('osm');
        // Re-enable 2D tracks when going back to 2D
        trackManager.setRenderOptions({ showLive: true });
      } else {
        // Going from 2D to 3D - default to no labels
        setShowTerrainLabels(false);
        // Hide 2D tracks when entering 3D mode
        trackManager.setRenderOptions({ showLive: false });
      }
      return !prev;
    });
  }, [trackManager]);

  // Handle labels toggle
  const toggleLabels = useCallback(() => {
    setShowTerrainLabels(prev => !prev);
  }, []);

  // Set up task markers - only when map is fully ready (2D mode)
  useTaskMarkers({
    map: mapFullyReady ? map : null,
    task: selectedTask,
    showTask,
    is3D  // Pass 3D state to handle rendering differently
  });

  // Task 3D visualization on terrain
  useTask3D({
    map: mapFullyReady ? map : null,
    task: selectedTask,
    enabled: showTask,
    is3D
  });


  // MVT layers are now loaded on-demand through TrackManager
  // No automatic loading on race start

  // Add controls in order (they stack from bottom to top)

  // Add fullscreen control (appears at the top)
  useFullscreenControl({
    map: mapFullyReady ? map : null,
    containerRef: mapContainerWrapper
  });

  // Add labels control (only shows in 3D mode, appears below fullscreen)
  useLabelsControl({
    map: mapFullyReady ? map : null,
    showLabels: showTerrainLabels,
    is3D,
    onToggle: toggleLabels
  });

  // Add 3D control to the map (appears below labels)
  use3DControl({
    map: mapFullyReady ? map : null,
    is3D,
    onToggle: toggle3D
  });

  // Add map style control (appears at the bottom, as the last button)
  // Hidden when in 3D mode to keep interface simple
  useMapStyleControl({
    map: mapFullyReady ? map : null,
    currentStyle: mapStyle,
    onStyleChange: (style) => {
      setMapStyle(style);
      switchToStyle(style as any);
    },
    is3D
  });

  // Map style changes are now handled by MapStyleManager
  // This provides better control and ensures tracks persist


  return (
    <div ref={mapContainerWrapper} className="map-container">
      {/* Time overlay - shows delayed UTC time on the map */}
      <TimeOverlay delayedTime={delayedTime} />

      {/* Loading overlays */}
      <LoadingOverlay
        isLoading={!mapFullyReady}
        text="Initializing map..."
      />
      <LoadingOverlay
        isLoading={mapFullyReady && pilotsLoading && waitingForPilots && pilots.size === 0 && !raceNotFound}
        text="Waiting for pilots..."
      />
      <LoadingOverlay
        isLoading={is3DLoading}
        text="Loading 3D experience..."
      />

      {/* Race not found screen */}
      <RaceNotFound show={raceNotFound} />

      {/* Status notifications */}
      <StatusNotification
        show={mapFullyReady && !pilotsLoading && pilots.size === 0 && !raceNotFound}
        message="No pilots are currently live in this race"
        type="info"
      />

      {/* 3D Terrain Component - only after map is fully ready */}
      <Map3DTerrain
        map={mapFullyReady ? map : null}
        enabled={is3D}
        showLabels={showTerrainLabels}
        onLoadingStateChange={setIs3DLoading}
        raceId={raceId}
        trackManager={trackManager}
        pilots={pilotsToUse} // Pass pilots for 3D rendering
      />

      {/* Pilot Sidebar - only show when map is ready */}
      {mapFullyReady && (
        <PilotSidebar
          pilots={pilotsToUse}
          selectedPilot={selectedPilot}
          followingPilot={followingPilot}
          onSelectPilot={setSelectedPilot}
          onCenterPilot={handleCenterPilot}
          onFollowPilot={setFollowingPilot}
          open={sidebarOpen}
          onOpenChange={setSidebarOpen}
        />
      )}

      <div ref={mapContainer} className="map" />
    </div>
  );
});

export default MapViewRefactored;